import React from 'react';
import { Box, Paper, Tabs, Tab, Typography, Button, Drawer, TextField, FormControl, InputLabel, Select, MenuItem, IconButton, Avatar, Divider, InputAdornment } from '@mui/material';
import AIGeneratePackagesDialog from '../projects/AIGeneratePackagesDialog';
import { offerStandaloneManageService, type OfferStandaloneInquiryDTO, type OfferStandalonePackageDTO, type OfferStandaloneReceivedOfferDTO } from '../../services/offerStandaloneManageService';
import OfferPackagesTable from './OfferPackagesTable';
import OfferBulkImportPackagesDialog from './OfferBulkImportPackagesDialog';
import OfferInquiriesTable from './OfferInquiriesTable';
import InquiriesTab from './InquiriesTab';
import OfferReceivedOffersTable from './OfferReceivedOffersTable';
import AddPackageForm from '../projects/AddPackageForm';
import AddInquiryForm from './AddInquiryForm';
import { useNavigate } from 'react-router-dom';
import { OfferStandalone, getStandaloneOfferUrlSegment } from '../../services/offerStandaloneService';
import { useNotification } from '../layouts/Layout';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`offer-details-tabpanel-${index}`}
      aria-labelledby={`offer-details-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 0, minHeight: '100vh' }}>{children}</Box>}
    </div>
  );
}

interface OfferDetailsProps {
  offer: OfferStandalone;
}

export default function OfferDetails({ offer }: OfferDetailsProps) {
  const [tabValue, setTabValue] = React.useState(0);
  const [manageTab, setManageTab] = React.useState(0);
  const [packages, setPackages] = React.useState<OfferStandalonePackageDTO[]>([]);
  const [inquiries, setInquiries] = React.useState<OfferStandaloneInquiryDTO[]>([]);
  const [receivedOffers, setReceivedOffers] = React.useState<OfferStandaloneReceivedOfferDTO[]>([]);
  const [loadingManage, setLoadingManage] = React.useState(false);
  const [openAddDialog, setOpenAddDialog] = React.useState(false);
  const [openAddPackageForm, setOpenAddPackageForm] = React.useState(false);
  const [selectedPackageData, setSelectedPackageData] = React.useState<any>(null);
  const [editingPackageId, setEditingPackageId] = React.useState<string | null>(null);
  const [openBulkDialog, setOpenBulkDialog] = React.useState(false);
  const [bulkItems, setBulkItems] = React.useState<any[]>([]);
  const [openAIPackagesDialog, setOpenAIPackagesDialog] = React.useState(false);
  const [openInquiryForm, setOpenInquiryForm] = React.useState(false);
  const [selectedPackageForInquiry, setSelectedPackageForInquiry] = React.useState<OfferStandalonePackageDTO | null>(null);

  // Formularze – wspólne stany dla trzech zakładek
  const [pkgName, setPkgName] = React.useState('');
  const [pkgSymbol, setPkgSymbol] = React.useState('');
  const [pkgType, setPkgType] = React.useState<'materiały' | 'usługi' | ''>('');

  const [inqEmail, setInqEmail] = React.useState('');
  const [inqSubject, setInqSubject] = React.useState('');
  const [inqPackageId, setInqPackageId] = React.useState('');

  const [roContractor, setRoContractor] = React.useState('');
  const [roPrice, setRoPrice] = React.useState<string>('');
  const [roType, setRoType] = React.useState<'materiały' | 'usługi' | ''>('');
  const [roInquiryId, setRoInquiryId] = React.useState('');
  const [roPackageId, setRoPackageId] = React.useState('');
  const [roStatus, setRoStatus] = React.useState<'Aktywna' | 'Zaakceptowana' | 'Odrzucona' | ''>('');
  const [roNotes, setRoNotes] = React.useState('');
  const [editingReceivedOfferId, setEditingReceivedOfferId] = React.useState<string | null>(null);
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleManageTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setManageTab(newValue);
  };

  // Skrót klawiszowy "§" (IntlBackslash) - przełączanie między głównymi zakładkami
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Nie działaj w polach edycyjnych
      const target = event.target as HTMLElement | null;
      const tag = (target?.tagName || '').toLowerCase();
      const isEditable = tag === 'input' || tag === 'textarea' || (target as any)?.isContentEditable;
      if (isEditable) return;

      // Sprawdź czy to skrót "§" (IntlBackslash) - główne zakładki
      if (
        !event.altKey && !event.ctrlKey && !event.metaKey &&
        (event.code === 'IntlBackslash' || event.key === '§' || event.key === '£' || event.key === '±')
      ) {
        event.preventDefault();
        event.stopPropagation();
        try {
          (event as any).stopImmediatePropagation?.();
        } catch {}
        const step = event.shiftKey ? -1 : 1;
        const enabledTabs = [0, 1, 5]; // Tylko włączone zakładki (Szczegóły zamówienia, Zarządzaj ofertowaniem, Kosztorys)
        const currentIndex = enabledTabs.indexOf(tabValue);
        if (currentIndex >= 0) {
          const nextIndex = (currentIndex + step + enabledTabs.length) % enabledTabs.length;
          const nextTab = enabledTabs[nextIndex];
          if (nextTab === 5) {
            const segment = getStandaloneOfferUrlSegment(offer);
            navigate(`/app/easy-estimate/offer/${encodeURIComponent(segment)}`);
          } else {
            setTabValue(nextTab);
          }
        }
      }

      // Skrót klawiszowy "`" (backtick) - przełączanie między podzakładkami w "Zarządzaj ofertowaniem"
      if (
        tabValue === 1 &&
        !event.altKey && !event.ctrlKey && !event.metaKey &&
        (event.key === '`' || event.code === 'Backquote')
      ) {
        event.preventDefault();
        event.stopPropagation();
        try {
          (event as any).stopImmediatePropagation?.();
        } catch {}
        const step = event.shiftKey ? -1 : 1;
        const manageTabsCount = 3; // Pakiety, Zapytania, Zestawienie ofert
        setManageTab((prev) => (prev + step + manageTabsCount) % manageTabsCount);
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [tabValue, offer, navigate]);

  React.useEffect(() => {
    const load = async () => {
      try {
        setLoadingManage(true);
        const [p, i, r] = await Promise.all([
        offerStandaloneManageService.getPackages(offer.id),
        offerStandaloneManageService.getInquiries(offer.id),
        offerStandaloneManageService.getReceivedOffers(offer.id),
      ]);
      setPackages(Array.isArray(p) ? p : []);
      setInquiries(Array.isArray(i) ? i : []);
      setReceivedOffers(Array.isArray(r) ? r : []);
    } catch (e: any) {
      console.error('load error:', e);
      showNotification('Błąd ładowania danych: ' + (e.message || 'Nieznany błąd'), 'error');
      // Ustaw puste tablice, aby uniknąć błędów renderowania
      setPackages([]);
      setInquiries([]);
      setReceivedOffers([]);
    } finally {
      setLoadingManage(false);
    }
    };
    load();
  }, [offer.id]);

  const refreshManageLists = async () => {
    try {
      const [p, i, r] = await Promise.all([
        offerStandaloneManageService.getPackages(offer.id),
        offerStandaloneManageService.getInquiries(offer.id),
        offerStandaloneManageService.getReceivedOffers(offer.id),
      ]);
      const pkgs = Array.isArray(p) ? p : [];
      console.debug('[OfferDetails] refreshManageLists OK, packages count:', pkgs.length);
      setPackages(pkgs);
      setInquiries(Array.isArray(i) ? i : []);
      setReceivedOffers(Array.isArray(r) ? r : []);
    } catch (e: any) {
      console.error('[OfferDetails] refreshManageLists error:', e?.response?.status, e?.message, e);
      showNotification('Błąd pobierania danych: ' + (e?.message || e?.response?.data?.error || 'Nieznany błąd'), 'error');
      // Nie czyść listy przy błędzie odświeżania – zachowaj poprzedni stan (np. świeżo utworzony pakiet)
      // setPackages([]) itd. powodowało „znikanie” pakietu gdy getPackages zwrócił błąd
    }
  };

  const handleOpenAdd = () => setOpenAddDialog(true);
  const handleCloseAdd = () => {
    setOpenAddDialog(false);
    setPkgName(''); setPkgSymbol(''); setPkgType('');
    setInqEmail(''); setInqSubject(''); setInqPackageId('');
    setRoContractor(''); setRoPrice(''); setRoType(''); setRoInquiryId(''); setRoPackageId(''); setRoStatus(''); setRoNotes('');
    setEditingReceivedOfferId(null);
  };

  // Handlery do formularza pakietu (z modułu Projektów) – dodajemy pakiet do oferty standalone
  const handleOpenAddPackage = () => { setSelectedPackageData(null); setEditingPackageId(null); setOpenAddPackageForm(true); };
  const handleCloseAddPackage = () => { setOpenAddPackageForm(false); setSelectedPackageData(null); setEditingPackageId(null); };
  const bulkFileInputRef = React.useRef<HTMLInputElement | null>(null);
  const handleBulkImportClick = () => {
    if (!bulkFileInputRef.current) {
      bulkFileInputRef.current = document.createElement('input');
      bulkFileInputRef.current.type = 'file';
      bulkFileInputRef.current.accept = 'application/json,.json';
      bulkFileInputRef.current.onchange = async (e: any) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        try {
          const text = await file.text();
          const json = JSON.parse(text);
          const list = Array.isArray(json) ? json : [json];
          if (list.length === 0) return;
          setBulkItems(list);
          setOpenBulkDialog(true);
        } catch {
          showNotification('Nie udało się odczytać pliku JSON (zbiorczy)', 'error');
        }
      };
    }
    bulkFileInputRef.current.value = '';
    bulkFileInputRef.current.click();
  };
  const handleAddPackage = async (packageData: any, files?: File[]) => {
    try {
      showNotification('Tworzenie pakietu…', 'info');
      const created = await offerStandaloneManageService.createPackage(offer.id, {
        name: packageData.name,
        package_symbol: packageData.symbol,
        package_type: packageData.package_type,
        description: packageData.description || undefined,
        parameters: packageData.parameters || undefined,
        estimated_value: packageData.estimatedValue ? Number(packageData.estimatedValue) : undefined,
        // Dodatkowe pola z formularza projektów możemy mapować w przyszłości
      }, files);
      showNotification('Pakiet utworzony.', 'success');
      if (created?.package_id) {
        setPackages(prev => {
          if (prev.some(p => p.package_id === created.package_id)) return prev;
          return [...prev, { ...created, inquiries_count: 0, offers_count: 0 }];
        });
      }
      await refreshManageLists();
    } finally {
      handleCloseAddPackage();
    }
  };

  const handleEditPackage = async (packageData: any, files?: File[]) => {
    if (!editingPackageId) return;
    try {
      showNotification('Zapisywanie zmian pakietu…', 'info');
      await offerStandaloneManageService.updatePackage(offer.id, editingPackageId, {
        name: packageData.name,
        package_type: packageData.package_type,
        description: packageData.description || undefined,
        parameters: packageData.parameters && packageData.parameters.trim() ? packageData.parameters.trim() : null,
        estimated_value: packageData.estimatedValue ? Number(packageData.estimatedValue) : undefined,
      });
      showNotification('Zapisano zmiany pakietu.', 'success');
      await refreshManageLists();
    } finally {
      handleCloseAddPackage();
    }
  };

  const handleSubmitAdd = async () => {
    try {
      if (manageTab === 0) {
        showNotification('Tworzenie pakietu…', 'info');
        const created = await offerStandaloneManageService.createPackage(offer.id, {
          name: pkgName,
          package_symbol: pkgSymbol || undefined,
          package_type: (pkgType || undefined) as any,
        });
        console.debug('[OfferDetails] createPackage OK:', created?.package_id);
        showNotification('Pakiet utworzony.', 'success');
        // Optymistycznie dodaj pakiet do listy – gdy refreshManageLists się nie powiedzie, pakiet nie zniknie
        if (created?.package_id) {
          setPackages(prev => {
            if (prev.some(p => p.package_id === created.package_id)) return prev;
            return [...prev, { ...created, inquiries_count: 0, offers_count: 0 }];
          });
        }
      } else if (manageTab === 1) {
        await offerStandaloneManageService.createInquiry(offer.id, {
          contractor_email: inqEmail,
          subject: inqSubject,
          package_id: inqPackageId || undefined,
        });
      } else if (manageTab === 2) {
        if (editingReceivedOfferId) {
          showNotification('Zapisywanie zmian oferty…', 'info');
          await offerStandaloneManageService.updateReceivedOffer(offer.id, editingReceivedOfferId, {
            contractor_name: roContractor,
            offered_price: roPrice ? Number(roPrice) : undefined,
            offer_type: (roType || undefined) as any,
            inquiry_id: roInquiryId || undefined,
            package_id: roPackageId || undefined,
            status: (roStatus || undefined) as any,
            notes: roNotes || undefined,
          });
          showNotification('Zapisano zmiany oferty.', 'success');
        } else {
          await offerStandaloneManageService.createReceivedOffer(offer.id, {
            contractor_name: roContractor,
            offered_price: roPrice ? Number(roPrice) : undefined,
            offer_type: (roType || undefined) as any,
            inquiry_id: roInquiryId || undefined,
            package_id: roPackageId || undefined,
            status: (roStatus || 'Aktywna') as any,
            notes: roNotes || undefined,
          });
        }
      }
      await refreshManageLists();
      handleCloseAdd();
    } catch (e) {
      showNotification('Nie udało się zapisać zmian.', 'error');
      handleCloseAdd();
    }
  };

  // Style pól formularza zgodne z AddPackageForm
  const textFieldSx = {
    '& .MuiInputLabel-root': {
      fontFamily: 'Roboto, sans-serif',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '16px',
      color: '#2D2D32'
    },
    '& .MuiOutlinedInput-root': {
      height: 32,
      backgroundColor: '#fff',
      borderRadius: '4px'
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
    '& .MuiInputBase-input': {
      padding: '8px 12px',
      boxSizing: 'border-box',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      color: '#2D2D32'
    },
    '& .MuiFormHelperText-root': { ml: 0 }
  } as const;

  const selectSx = {
    '& .MuiOutlinedInput-root': {
      height: 32,
      backgroundColor: '#fff',
      borderRadius: '4px'
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
    '&:has(.MuiSelect-root[aria-expanded="true"]) .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiSelect-select': {
      padding: '8px 12px',
      boxSizing: 'border-box',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      color: '#2D2D32'
    }
  } as const;

  const FieldLabel: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32', mb: 0.5 }}>
      {text}
      {required && <Box component="span" sx={{ color: '#d32f2f' }}> *</Box>}
    </Typography>
  );

  const formatDate = (date?: string) => {
    if (!date) return '-';
    try {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return date;
    }
  };

  const formatNumber = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  };

  const calculateDaysToDeadline = (deadline?: string): number | null => {
    if (!deadline) return null;
    try {
      const deadlineDate = new Date(deadline);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      deadlineDate.setHours(0, 0, 0, 0);
      const diffTime = deadlineDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays >= 0 ? diffDays : 0;
    } catch {
      return null;
    }
  };

  const calculateTodayPosition = (): number | null => {
    try {
      const startDate = offer.created_at ? new Date(offer.created_at) : null;
      const endDate = offer.submission_deadline ? new Date(offer.submission_deadline) : null;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (!startDate || !endDate) return null;
      
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      
      const totalRange = endDate.getTime() - startDate.getTime();
      if (totalRange <= 0) return null;
      
      const todayOffset = today.getTime() - startDate.getTime();
      
      // Jeśli dzisiaj jest przed rozpoczęciem, zwróć 0%
      if (todayOffset < 0) return 0;
      // Jeśli dzisiaj jest po zakończeniu, zwróć 100%
      if (todayOffset > totalRange) return 100;
      
      // Oblicz procent pozycji
      const percentage = (todayOffset / totalRange) * 100;
      return percentage;
    } catch {
      return null;
    }
  };

  return (
    <>
    <Paper sx={{ 
      mt: 0, 
      p: 0, 
      overflow: 'hidden',
      // Dopasowanie do tła aplikacji – bez widocznej „karty”
      backgroundColor: '#FAFAFA',
      boxShadow: 'none',
      border: '1px solid #FAFAFA'
    }}>
      {/* Pasek zakładek głównych */}
      <Box
        data-tab-scope="main"
        sx={{ mt: '28px', mb: 2, borderBottom: '1px solid #EDEDED', display: 'flex', gap: 3, alignItems: 'flex-end' }}
      >
        {[
          'Szczegóły',
          'Zarządzaj ofertowaniem',
          'Plan ofertowania',
          'Harmonogram ofertowania',
          'Przepływy pieniężne',
          'Kosztorys'
        ].map((label, idx) => {
          const isDisabled = idx === 2 || idx === 3 || idx === 4;
          return (
            <Box
              key={label}
              data-tab="main"
              aria-selected={tabValue === idx ? 'true' : 'false'}
              onClick={() => {
                if (idx === 5) {
                  const segment = getStandaloneOfferUrlSegment(offer);
                  navigate(`/app/easy-estimate/offer/${encodeURIComponent(segment)}`);
                  return;
                }
                if (!isDisabled) handleTabChange({} as React.SyntheticEvent, idx);
              }}
              sx={{
                position: 'relative',
                pb: 1,
                cursor: 'pointer',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '16px',
                color: '#020242',
                fontWeight: tabValue === idx ? 500 : 400,
                userSelect: 'none'
              }}
            >
              {label}
              {tabValue === idx && (
                <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 2, bgcolor: '#000000' }} />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Stary Tabs - ukryty, zachowany dla kompatybilności */}
      <Box sx={{ display: 'none' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="offer details tabs" 
          variant="scrollable" 
          scrollButtons="auto"
          sx={{ 
            px: 2,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 'medium',
              fontSize: '0.9rem',
              minWidth: 100
            },
            '& .Mui-selected': (t) => ({
              color: t.palette.primary.main,
              fontWeight: 'bold'
            }),
            '& .MuiTabs-indicator': (t) => ({
              backgroundColor: t.palette.primary.main
            }),
            '& .Mui-disabled': (t) => ({
              color: t.palette.text.disabled,
              opacity: 1
            })
          }}
        >
          <Tab label="Szczegóły zamówienia" />
          <Tab label="Zarządzaj ofertowaniem" />
          <Tab label="Plan ofertowania" disabled />
          <Tab label="Harmonogram ofertowania" disabled />
          <Tab label="Przepływy pieniężne" disabled />
          <Tab label="Kosztorys" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ px: 0 }}>
          {/* Nowy moduł: zdjęcie Street View + podstawowe informacje */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', mb: 2 }}>
            {/* Lewy blok: zdjęcie Street View + podstawowe informacje */}
            <Box
              sx={{
                display: 'flex',
                border: '1px solid #EDEDED',
                borderRadius: '4px',
                overflow: 'hidden',
                minHeight: 208,
                width: 'calc(100% - 230px)',
                bgcolor: '#fff'
              }}
            >
              {/* Zdjęcie Street View */}
              <Box sx={{ position: 'relative', width: 291, flexShrink: 0, backgroundColor: '#D9D9D9' }}>
                {(() => {
                  const address = offer.investment_address || '';
                  console.log('Street View address:', address);
                  
                  if (!address) {
                    return (
                      <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box
                          component="img"
                          src="/assets/Rectangle9.png"
                          alt="Brak zdjęcia"
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      </Box>
                    );
                  }
                  
                  // Google Maps Street View Static API - użyj proxy przez backend lub bezpośrednio
                  // Format adresu: "PLAC GEN. JÓZEFA BEMA 8 07-410 OSTROŁĘKA"
                  const streetViewUrl = `https://maps.googleapis.com/maps/api/streetview?size=400x400&location=${encodeURIComponent(address)}&fov=90&heading=0&pitch=0`;
                  console.log('Street View URL:', streetViewUrl);
                  
                  return (
                    <Box
                      component="img"
                      src={streetViewUrl}
                      alt="Street View"
                      crossOrigin="anonymous"
                      onError={(e: any) => {
                        console.error('Street View image error - falling back to placeholder');
                        const target = e.currentTarget;
                        if (target.src !== '/assets/Rectangle9.png') {
                          target.src = '/assets/Rectangle9.png';
                        }
                      }}
                      onLoad={(e: any) => {
                        // Sprawdź czy obrazek nie jest obrazkiem błędu Google Maps
                        const img = e.currentTarget;
                        if (img.naturalWidth === 0 || img.naturalHeight === 0) {
                          console.error('Street View image has zero dimensions - likely an error image');
                          img.src = '/assets/Rectangle9.png';
                          return;
                        }
                        console.log('Street View image loaded successfully:', img.naturalWidth, 'x', img.naturalHeight);
                      }}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  );
                })()}
              </Box>

              {/* Prawy panel treści */}
              <Box sx={{ flex: 1, backgroundColor: '#fff', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: '24px', pt: '24px', pb: 0 }}>
                  <Box sx={{ display: 'grid', rowGap: 0 }}>
                    {[
                      { 
                        label: 'Przygotowuje ofertę (PO)', 
                        value: (() => {
                          if (offer.offer_preparer === '__AI__') return 'AI';
                          if (offer.offer_preparer_name) return offer.offer_preparer_name;
                          return '-';
                        })(),
                        isUser: true,
                        userName: offer.offer_preparer_name,
                        userAvatar: offer.offer_preparer_avatar_url,
                        isAI: offer.offer_preparer === '__AI__'
                      },
                      { label: 'Zamawiający', value: offer.client_name || '-' },
                      { label: 'Adres zamawiającego', value: offer.client_address || '-' },
                      { label: 'Nazwa zamówienia', value: offer.order_name || '-' },
                      { label: 'Adres inwestycji', value: offer.investment_address || '-' }
                    ].map((row, idx) => {
                      const isOrderName = row.label === 'Nazwa zamówienia';
                      return (
                      <Box
                        key={row.label}
                        sx={{
                          display: 'flex',
                          alignItems: isOrderName ? 'flex-start' : 'center',
                          minHeight: isOrderName ? 'auto' : 32,
                          height: isOrderName ? 'auto' : 32,
                          backgroundColor: idx % 2 === 1 ? '#F9F9F9' : '#FFFFFF',
                          px: 0,
                          py: isOrderName ? '8px' : 0
                        }}
                      >
                        <Box sx={{ width: 180, pr: 2, pt: isOrderName ? '2px' : 0, flexShrink: 0 }}>
                          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966', whiteSpace: 'nowrap' }}>
                            {row.label}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: isOrderName ? 'flex-start' : 'center', gap: 1 }}>
                          {row.isUser && row.isAI ? (
                            <>
                              <Avatar sx={{ width: 20, height: 20, bgcolor: 'transparent', p: 0 }}>
                                <Box component="img" src="/assets/ai_badge_unitresco.svg" alt="AI" sx={{ width: 20, height: 20, display: 'block' }} />
                              </Avatar>
                              <Typography
                                sx={{
                                  fontFamily: 'Roboto, sans-serif',
                                  fontSize: '14px',
                                  color: '#020242',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                AI
                              </Typography>
                            </>
                          ) : row.isUser && (row.userName || row.userAvatar) ? (
                            <>
                              <Avatar 
                                src={row.userAvatar || undefined} 
                                alt={row.userName || 'Użytkownik'}
                                sx={{ width: 20, height: 20, fontSize: '0.65rem' }}
                              >
                                {(row.userName || '?').substring(0, 1).toUpperCase()}
                              </Avatar>
                              <Typography
                                sx={{
                                  fontFamily: 'Roboto, sans-serif',
                                  fontSize: '14px',
                                  color: '#020242',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                                title={row.userName || 'Użytkownik'}
                              >
                                {row.userName || 'Użytkownik'}
                              </Typography>
                            </>
                          ) : (
                            <Typography
                              sx={{
                                fontFamily: 'Roboto, sans-serif',
                                fontSize: '14px',
                                color: '#020242',
                                overflow: isOrderName ? 'visible' : 'hidden',
                                textOverflow: isOrderName ? 'clip' : 'ellipsis',
                                whiteSpace: isOrderName ? 'normal' : 'nowrap',
                                wordBreak: isOrderName ? 'break-word' : 'normal'
                              }}
                              title={String(row.value)}
                            >
                              {row.value}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )})}
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Prawy kafelek: Do złożenia oferty */}
            {(() => {
              const daysToDeadline = calculateDaysToDeadline(offer.submission_deadline);
              return (
                <Box
                  sx={{
                    width: 214.5,
                    minWidth: 214,
                    bgcolor: '#fff',
                    border: '1px solid #EDEDED',
                    borderRadius: '4px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Typography sx={{ color: '#595966', fontSize: 14, px: '24px', pt: '24px' }}>Do złożenia oferty</Typography>
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2, px: '24px', pb: '24px' }}>
                    <Box sx={{ width: 80, height: 80, display: 'grid', placeItems: 'center' }}>
                      <Box
                        component="img"
                        src="/assets/icons/timer_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg"
                        alt="Timer"
                        sx={{ width: 48, height: 48 }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Typography sx={{ fontSize: 32, lineHeight: '38px', color: '#020242' }}>
                        {daysToDeadline !== null ? daysToDeadline : '-'}
                      </Typography>
                      <Typography sx={{ fontSize: 14, lineHeight: '16px', color: '#020242' }}>
                        {daysToDeadline !== null ? (daysToDeadline === 1 ? 'dzień' : 'dni') : 'brak terminu'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })()}
          </Box>

          {/* Kafelek: Harmonogram ofertowania */}
          <Box sx={{ bgcolor: '#fff', border: '1px solid #EDEDED', borderRadius: '4px', p: 0, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: '24px', pt: '24px', pb: '16px' }}>
              <Box
                component="img"
                src="/assets/icons/timeline_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg"
                alt="Timeline"
                sx={{ width: 24, height: 24 }}
              />
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '16px',
                  lineHeight: '19px',
                  fontWeight: 500,
                  color: '#2D2D32'
                }}
              >
                Harmonogram ofertowania
              </Typography>
            </Box>
            <Box sx={{ px: '24px', pb: '24px' }}>
              {(() => {
                const hasSiteInspection = offer.site_inspection === 'Tak' && offer.site_inspection_date;
                return (
                  <Box sx={{ position: 'relative', width: '100%', pt: 4 }}>
                    {/* Pozioma linia z punktami */}
                    <Box sx={{ position: 'relative', width: '100%', height: 2, bgcolor: '#EDEDED', mb: 2 }}>
                      {/* Linia aktywna */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          height: '100%',
                          bgcolor: '#1F1FB0',
                          width: '100%',
                          transition: 'width 0.3s ease'
                        }}
                      />
                      {/* Punkty harmonogramu - dokładnie na środku osi */}
                      <Box sx={{ position: 'absolute', top: '50%', left: 0, right: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', transform: 'translateY(-50%)' }}>
                        {/* Punkt 1: Rozpoczęcie ofertowania */}
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: '#1F1FB0',
                            border: '2px solid #fff',
                            boxShadow: '0 0 0 2px #1F1FB0',
                            flexShrink: 0,
                            zIndex: 1
                          }}
                        />

                        {/* Punkt 2: Termin wizji lokalnej */}
                        {hasSiteInspection ? (
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: '#1F1FB0',
                              border: '2px solid #fff',
                              boxShadow: '0 0 0 2px #1F1FB0',
                              flexShrink: 0,
                              zIndex: 1
                            }}
                          />
                        ) : (
                          <Box sx={{ flex: 1 }} />
                        )}

                        {/* Punkt 3: Składanie ofert */}
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: '#1F1FB0',
                            border: '2px solid #fff',
                            boxShadow: '0 0 0 2px #1F1FB0',
                            flexShrink: 0,
                            zIndex: 1
                          }}
                        />
                      </Box>
                      {/* Czerwony punkt - dzisiaj */}
                      {(() => {
                        const todayPosition = calculateTodayPosition();
                        if (todayPosition === null) return null;
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: `${todayPosition}%`,
                              transform: 'translate(-50%, -50%)',
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: '#B40003',
                              border: '2px solid #fff',
                              boxShadow: '0 0 0 2px #B40003',
                              zIndex: 2,
                              cursor: 'pointer'
                            }}
                          />
                        );
                      })()}
                      {/* Opisy nad osią */}
                      <Box sx={{ position: 'absolute', bottom: '100%', left: 0, right: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        {/* Opis 1: Rozpoczęcie - puste miejsce */}
                        <Box sx={{ flex: 1 }} />
                        
                        {/* Opis 2: Termin wizji lokalnej - nad osią */}
                        {hasSiteInspection ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                            <Typography
                              sx={{
                                fontFamily: 'Roboto, sans-serif',
                                fontSize: '12px',
                                color: '#595966',
                                fontWeight: 500,
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Termin wizji lokalnej
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: 'Roboto, sans-serif',
                                fontSize: '14px',
                                color: '#020242',
                                fontWeight: 400,
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {formatDate(offer.site_inspection_date)}
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ flex: 1 }} />
                        )}
                        
                        {/* Opis 3: Składanie ofert - puste miejsce */}
                        <Box sx={{ flex: 1 }} />
                      </Box>
                    </Box>
                    {/* Opisy pod osią */}
                    <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                      {/* Opis 1: Rozpoczęcie - pod osią */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Roboto, sans-serif',
                            fontSize: '12px',
                            color: '#595966',
                            fontWeight: 500,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Rozpoczęcie
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'Roboto, sans-serif',
                            fontSize: '14px',
                            color: '#020242',
                            fontWeight: 400,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {formatDate(offer.created_at)}
                        </Typography>
                      </Box>

                      {/* Opis 2: Termin wizji lokalnej - puste miejsce pod osią */}
                      {hasSiteInspection ? (
                        <Box sx={{ flex: 1 }} />
                      ) : null}

                      {/* Opis 3: Składanie ofert - pod osią, wyrównany do prawej */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flex: 1 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Roboto, sans-serif',
                            fontSize: '12px',
                            color: '#595966',
                            fontWeight: 500,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Składanie ofert
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'Roboto, sans-serif',
                            fontSize: '14px',
                            color: '#020242',
                            fontWeight: 400,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {formatDate(offer.submission_deadline)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          </Box>

          {/* Sekcja: Terminy i wartości + Dokumenty i media */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            {/* Kafelek: Terminy i wartości */}
            <Box sx={{ bgcolor: '#fff', border: '1px solid #EDEDED', borderRadius: '8px', p: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '16px',
                  lineHeight: '19px',
                  fontWeight: 500,
                  color: '#2D2D32',
                  px: '24px',
                  pt: '24px',
                  pb: '8px'
                }}
              >
                Terminy i wartości
              </Typography>
              {[
                { label: 'Wizja lokalna', value: offer.site_inspection, isUser: false },
                ...(offer.site_inspection === 'Tak' ? [{ label: 'Termin wizji lokalnej (TWL)', value: formatDate(offer.site_inspection_date), isUser: false }] : []),
                { label: 'Termin składania oferty (TSO)', value: formatDate(offer.submission_deadline), isUser: false },
                { label: 'Szacunkowa wartość zamówienia (SWZ)', value: formatNumber(offer.estimated_order_value), isUser: false },
                { label: 'Wadium', value: formatNumber(offer.bid_bond), isUser: false }
              ].map((row, idx) => (
                <Box
                  key={row.label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: 48,
                    backgroundColor: idx % 2 === 1 ? '#F9F9F9' : '#FFFFFF',
                    px: '24px'
                  }}
                >
                  <Box sx={{ width: 180, pr: 2 }}>
                    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966' }}>
                      {row.label}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#020242' }} title={String(row.value)}>
                      {row.value}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Kafelek: Dokumenty i media */}
            <Box sx={{ bgcolor: '#fff', border: '1px solid #EDEDED', borderRadius: '8px', p: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '16px',
                  lineHeight: '19px',
                  fontWeight: 500,
                  color: '#2D2D32',
                  px: '24px',
                  pt: '24px',
                  pb: '8px'
                }}
              >
                Dokumenty i media
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: '24px', pb: '16px' }}>
                {offer.google_drive_link ? (
                  <Box
                    component="a"
                    href={offer.google_drive_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => { e.preventDefault(); window.open(offer.google_drive_link!, '_blank'); }}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 1.5,
                      height: 32,
                      borderRadius: '4px',
                      backgroundColor: '#F5F5FF',
                      color: '#1F1FB0',
                      textDecoration: 'none',
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    Google Drive
                  </Box>
                ) : (
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#595966', px: '24px', pb: '16px' }}>
                    Brak dokumentów
                  </Typography>
                )}
                {offer.client_website && (
                  <Box
                    component="a"
                    href={offer.client_website}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => { e.preventDefault(); window.open(offer.client_website!, '_blank'); }}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 1.5,
                      height: 32,
                      borderRadius: '4px',
                      backgroundColor: '#F5F5FF',
                      color: '#1F1FB0',
                      textDecoration: 'none',
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    Strona zamawiającego
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Sekcja: Kwalifikacje wykonawcy */}
          {(offer.technical_experience || offer.personnel_potential || offer.economic_situation) && (
            <Box sx={{ bgcolor: '#fff', border: '1px solid #EDEDED', borderRadius: '4px', p: 0, mt: 2 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '16px',
                  lineHeight: '19px',
                  fontWeight: 500,
                  color: '#2D2D32',
                  px: '24px',
                  pt: '24px',
                  pb: '8px'
                }}
              >
                Kwalifikacje wykonawcy
              </Typography>
              {offer.technical_experience && (
                <Box sx={{ px: '24px', pb: 2 }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966', mb: 1 }}>
                    Doświadczenie techniczne
                  </Typography>
                  <Box sx={{ p: 2, backgroundColor: '#F9F9F9', borderRadius: '4px', border: '1px solid #EDEDED' }}>
                    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#020242', whiteSpace: 'pre-wrap' }}>
                      {offer.technical_experience}
                    </Typography>
                  </Box>
                </Box>
              )}
              {offer.personnel_potential && (
                <Box sx={{ px: '24px', pb: 2 }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966', mb: 1 }}>
                    Potencjał osobowy
                  </Typography>
                  <Box sx={{ p: 2, backgroundColor: '#F9F9F9', borderRadius: '4px', border: '1px solid #EDEDED' }}>
                    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#020242', whiteSpace: 'pre-wrap' }}>
                      {offer.personnel_potential}
                    </Typography>
                  </Box>
                </Box>
              )}
              {offer.economic_situation && (
                <Box sx={{ px: '24px', pb: '24px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966', mb: 1 }}>
                    Sytuacja ekonomiczna
                  </Typography>
                  <Box sx={{ p: 2, backgroundColor: '#F9F9F9', borderRadius: '4px', border: '1px solid #EDEDED' }}>
                    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#020242', whiteSpace: 'pre-wrap' }}>
                      {offer.economic_situation}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ px: 0 }}>
          <Box sx={{ mb: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '2px', height: 40, mb: 0 }}>
              <Tabs
                value={manageTab}
                onChange={(_, v) => setManageTab(v)}
                aria-label="manage offer subtabs"
                sx={{
                  minHeight: 40,
                  height: 40,
                  flexGrow: 1,
                  '& .MuiTabs-indicator': { display: 'none' },
                  '& .MuiTabs-flexContainer': { gap: '2px' }
                }}
              >
                <Tab
                  disableRipple
                  value={0}
                  label={`Pakiety (${packages.length})`}
                  sx={{
                    textTransform: 'none',
                    color: '#020242',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    fontWeight: 400,
                    px: 3,
                    minHeight: 32,
                    height: 32,
                    mt: '8px',
                    bgcolor: '#F1F1F6',
                    borderRadius: '4px 4px 0 0',
                    '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                    '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
                  }}
                />
                <Tab
                  disableRipple
                  value={1}
                  label={`Zapytania (${inquiries.length})`}
                  sx={{
                    textTransform: 'none',
                    color: '#020242',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    fontWeight: 400,
                    px: 3,
                    minHeight: 32,
                    height: 32,
                    mt: '8px',
                    bgcolor: '#F1F1F6',
                    borderRadius: '4px 4px 0 0',
                    '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                    '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
                  }}
                />
                <Tab
                  disableRipple
                  value={2}
                  label={`Zestawienie ofert (${receivedOffers.length})`}
                  sx={{
                    textTransform: 'none',
                    color: '#020242',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    fontWeight: 400,
                    px: 3,
                    minHeight: 32,
                    height: 32,
                    mt: '8px',
                    bgcolor: '#F1F1F6',
                    borderRadius: '4px 4px 0 0',
                    '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                    '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
                  }}
                />
              </Tabs>
            </Box>
            
            {manageTab === 0 && (
            loadingManage ? (
              <Typography variant="body2">Ładowanie…</Typography>
            ) : (
              <OfferPackagesTable
                onAddClick={handleOpenAddPackage} 
                rows={packages}
                onEdit={async (row) => {
                  // Otwórz formularz edycji z danymi pakietu
                  setEditingPackageId(row.package_id);
                  setSelectedPackageData({
                    symbol: row.package_symbol || '',
                    name: row.name,
                    package_type: row.package_type || '',
                    description: (row as any).description || '',
                    parameters: (row as any).parameters || '',
                    estimatedValue: (row as any).estimated_value ? String((row as any).estimated_value) : '',
                  });
                  setOpenAddPackageForm(true);
                }}
                onDelete={async (row) => {
                  try {
                    await offerStandaloneManageService.deletePackage(offer.id, row.package_id);
                    showNotification('Pakiet usunięty.', 'success');
                    await refreshManageLists();
                  } catch (e: any) {
                    showNotification('Nie udało się usunąć pakietu.', 'error');
                  }
                }}
                onCreateInquiry={(row) => {
                  setSelectedPackageForInquiry(row);
                  setOpenInquiryForm(true);
                }}
                onOpenDocumentation={(row) => { if (row.documentation_link) window.open(row.documentation_link, '_blank'); else showNotification('Brak linku do dokumentacji całkowitej', 'info'); }}
                onOpenPackageDocumentation={(row) => { if (row.package_documentation_link) window.open(row.package_documentation_link, '_blank'); else showNotification('Brak linku do dokumentacji pakietu', 'info'); }}
              />
            )
          )}
          {manageTab === 1 && (
            <InquiriesTab 
              projectName={offer.order_name}
              offerId={offer.id}
              offerDisplayId={(offer as any).offer_id || (offer.offer_sequence_number && offer.created_at ? `OF.${offer.offer_sequence_number}.${new Date(offer.created_at).getFullYear()}` : offer.id.substring(0, 8))}
              hasCollectifileBoard={!!(offer as any).collectifile_board_id || !!(offer as any).miro_link}
              onRefreshData={refreshManageLists}
              onOpenAddReceivedOffer={(inquiryData) => {
                // Wypełnij pola formularza danymi z zapytania
                setEditingReceivedOfferId(null);
                setRoInquiryId(inquiryData.inquiryId);
                setRoPackageId(inquiryData.packageId || '');
                setRoContractor(inquiryData.contractorEmail);
                setRoPrice('');
                setRoType('');
                setRoStatus('Aktywna');
                setRoNotes('');
                // Przełącz na zakładkę ofert i otwórz drawer
                setManageTab(2);
                setOpenAddDialog(true);
              }}
            />
          )}
          {manageTab === 2 && (
            loadingManage ? <Typography variant="body2">Ładowanie…</Typography> : <OfferReceivedOffersTable 
              onAddClick={() => {
                setEditingReceivedOfferId(null);
                setRoContractor('');
                setRoPrice('');
                setRoType('');
                setRoInquiryId('');
                setRoPackageId('');
                setRoStatus('Aktywna');
                setRoNotes('');
                setOpenAddDialog(true);
              }}
              rows={receivedOffers}
              onEdit={async (row) => {
                setEditingReceivedOfferId(row.offer_id);
                setRoContractor(row.contractor_name || '');
                setRoPrice(row.offered_price ? String(row.offered_price) : '');
                setRoType(row.offer_type || '');
                setRoInquiryId(row.inquiry_id || '');
                setRoPackageId(row.package_id || '');
                setRoStatus(row.status || 'Aktywna');
                setRoNotes(row.notes || '');
                setOpenAddDialog(true);
              }}
            />
          )}
          </Box>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box sx={{ px: 3 }}>
          <Typography variant="body1">Plan ofertowania dla tej oferty jest w trakcie budowy.</Typography>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Box sx={{ px: 3 }}>
          <Typography variant="body1">Harmonogram ofertowania dla tej oferty jest w trakcie budowy.</Typography>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Box sx={{ px: 3 }}>
          <Typography variant="body1">Przepływy pieniężne dla tej oferty są w trakcie budowy.</Typography>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        <Box sx={{ px: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Moduł kosztorysowania jest dostępny w osobnej zakładce.
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate('/app/estimates')}
            sx={{ backgroundColor: '#003366', '&:hover': { backgroundColor: '#002244' } }}
          >
            Przejdź do kosztorysowania
          </Button>
        </Box>
      </TabPanel>
    </Paper>

    {/* Drawer dodawania */}
    <Drawer
      anchor="right"
      open={openAddDialog}
      onClose={handleCloseAdd}
      PaperProps={{ sx: { width: 680, maxWidth: '100vw', borderRadius: 0 } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
            {manageTab === 0 ? 'Dodaj pakiet' : manageTab === 1 ? 'Dodaj zapytanie' : (editingReceivedOfferId ? 'Edytuj ofertę (zestawienie)' : 'Dodaj ofertę (zestawienie)')}
          </Typography>
          <IconButton 
            onClick={handleCloseAdd} 
            size="small"
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 30,
              minHeight: 30,
              '& > *': {
                pointerEvents: 'none',
              },
            }}
          >
            <Box 
              component="img" 
              src="/assets/close.svg" 
              alt="Zamknij" 
              sx={{ 
                width: 13, 
                height: 13, 
                display: 'block',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }} 
            />
          </IconButton>
        </Box>
        <Box sx={{ p: 4, pt: 3, flex: 1, overflowY: 'auto' }}>
          <Box sx={{ display: 'grid', gap: 1.5 }}>
        {manageTab === 0 && (
          <>
            <Box>
              <FieldLabel text="Nazwa pakietu" />
              <TextField value={pkgName} onChange={(e) => setPkgName(e.target.value)} fullWidth size="small" sx={textFieldSx} />
            </Box>
            <Box>
              <FieldLabel text="Symbol pakietu" />
              <TextField value={pkgSymbol} onChange={(e) => setPkgSymbol(e.target.value)} fullWidth size="small" sx={textFieldSx} />
            </Box>
            <Box>
              <FieldLabel text="Typ pakietu" />
              <FormControl fullWidth size="small" sx={selectSx}>
                <Select value={pkgType} onChange={(e) => setPkgType(e.target.value as any)} displayEmpty renderValue={(val) => (val ? String(val) : 'Wybierz z listy')}>
                  <MenuItem value="">Brak</MenuItem>
                  <MenuItem value="materiały">materiały</MenuItem>
                  <MenuItem value="usługi">usługi</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </>
        )}

        {manageTab === 1 && (
          <>
            <Box>
              <FieldLabel text="Pakiet" />
              <FormControl fullWidth size="small" sx={selectSx}>
                <Select value={inqPackageId} onChange={(e) => setInqPackageId(e.target.value)} displayEmpty renderValue={(val) => (val ? packages.find(p => p.package_id === val)?.name || 'Wybierz z listy' : 'Bez pakietu')}>
                  <MenuItem value="">Bez pakietu</MenuItem>
                  {packages.map(p => (
                    <MenuItem key={p.package_id} value={p.package_id}>{p.package_symbol ? `${p.package_symbol} — ` : ''}{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box>
              <FieldLabel text="E-mail kontrahenta" />
              <TextField type="email" value={inqEmail} onChange={(e) => setInqEmail(e.target.value)} fullWidth size="small" sx={textFieldSx} />
            </Box>
            <Box>
              <FieldLabel text="Temat" />
              <TextField value={inqSubject} onChange={(e) => setInqSubject(e.target.value)} fullWidth size="small" sx={textFieldSx} />
            </Box>
          </>
        )}

        {manageTab === 2 && (
          <>
            <Box>
              <FieldLabel text="Zapytanie" />
              <FormControl fullWidth size="small" sx={selectSx}>
                <Select value={roInquiryId} onChange={(e) => setRoInquiryId(e.target.value)} displayEmpty renderValue={(val) => (val ? inquiries.find(i => i.inquiry_id === val)?.subject || 'Wybierz z listy' : 'Bez powiązania')}>
                  <MenuItem value="">Bez powiązania</MenuItem>
                  {inquiries.map(i => (
                    <MenuItem key={i.inquiry_id} value={i.inquiry_id}>{i.subject}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box>
              <FieldLabel text="Pakiet" />
              <FormControl fullWidth size="small" sx={selectSx}>
                <Select value={roPackageId} onChange={(e) => setRoPackageId(e.target.value)} displayEmpty renderValue={(val) => (val ? packages.find(p => p.package_id === val)?.name || 'Wybierz z listy' : 'Bez pakietu')}>
                  <MenuItem value="">Bez pakietu</MenuItem>
                  {packages.map(p => (
                    <MenuItem key={p.package_id} value={p.package_id}>{p.package_symbol ? `${p.package_symbol} — ` : ''}{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box>
              <FieldLabel text="Wykonawca / Dostawca" />
              <TextField value={roContractor} onChange={(e) => setRoContractor(e.target.value)} fullWidth size="small" sx={textFieldSx} />
            </Box>
            <Box>
              <FieldLabel text="Typ" />
              <FormControl fullWidth size="small" sx={selectSx}>
                <Select value={roType} onChange={(e) => setRoType(e.target.value as any)} displayEmpty renderValue={(val) => (val ? String(val) : 'Wybierz z listy')}>
                  <MenuItem value="">Brak</MenuItem>
                  <MenuItem value="materiały">materiały</MenuItem>
                  <MenuItem value="usługi">usługi</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box>
              <FieldLabel text="Cena ofertowa (PLN)" />
              <TextField type="number" value={roPrice} onChange={(e) => setRoPrice(e.target.value)} fullWidth size="small" sx={textFieldSx} />
            </Box>
            <Box>
              <FieldLabel text="Status" />
              <FormControl fullWidth size="small" sx={selectSx}>
                <Select value={roStatus} onChange={(e) => setRoStatus(e.target.value as any)} displayEmpty renderValue={(val) => (val ? String(val) : 'Wybierz z listy')}>
                  <MenuItem value="Aktywna">Aktywna</MenuItem>
                  <MenuItem value="Zaakceptowana">Zaakceptowana</MenuItem>
                  <MenuItem value="Odrzucona">Odrzucona</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box>
              <FieldLabel text="Uwagi" />
              <TextField 
                value={roNotes} 
                onChange={(e) => setRoNotes(e.target.value)} 
                fullWidth 
                multiline 
                minRows={3}
                placeholder="Wpisz uwagi dotyczące oferty..."
                sx={{
                  ...textFieldSx,
                  '& .MuiOutlinedInput-root': {
                    ...textFieldSx['& .MuiOutlinedInput-root'],
                    height: 'auto',
                    alignItems: 'flex-start'
                  },
                  '& .MuiInputBase-input': {
                    ...textFieldSx['& .MuiInputBase-input'],
                    padding: 0
                  }
                }}
              />
            </Box>
          </>
        )}
          </Box>
        </Box>
        <Divider />
        <Box sx={{ px: 4, py: 2, display: 'flex', justifyContent: 'space-between', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)' }}>
          <Box>
            {manageTab === 2 && editingReceivedOfferId && (
              <Button 
                onClick={async () => {
                  if (editingReceivedOfferId && window.confirm('Czy na pewno chcesz usunąć tę ofertę?')) {
                    try {
                      await offerStandaloneManageService.deleteReceivedOffer(offer.id, editingReceivedOfferId);
                      showNotification('Oferta usunięta.', 'success');
                      await refreshManageLists();
                      handleCloseAdd();
                    } catch (e: any) {
                      showNotification('Nie udało się usunąć oferty.', 'error');
                    }
                  }
                }} 
                variant="outlined" 
                color="error"
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  textTransform: 'none'
                }}
              >
                Usuń
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleCloseAdd}
              variant="text"
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                textTransform: 'none',
                color: '#2D2D32',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              Anuluj
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitAdd}
              sx={{
                backgroundColor: '#020242',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#030360'
                }
              }}
            >
              Zapisz
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>

    {/* Formularz dodawania pakietu (spójny z modułem projektów) */}
    <AddPackageForm
      open={openAddPackageForm}
      onClose={handleCloseAddPackage}
      onAdd={handleAddPackage}
      onEdit={handleEditPackage}
      editMode={Boolean(editingPackageId)}
      packageData={selectedPackageData}
      importedFilePaths={selectedPackageData?.__files_paths || []}
    />

    <OfferBulkImportPackagesDialog
      open={openBulkDialog}
      onClose={() => setOpenBulkDialog(false)}
      offerId={offer.id}
      initialPackages={bulkItems}
      onCompleted={async (summary: any) => {
        await refreshManageLists();
        const { successes = 0, failures = 0, total = 0 } = summary || ({} as any);
        const msg = `Import zakończony. Utworzono: ${successes}/${total}${failures ? `, błędy: ${failures}` : ''}.`;
        showNotification(msg, failures ? 'warning' : 'success');
      }}
    />

    <AIGeneratePackagesDialog
      open={openAIPackagesDialog}
      onClose={() => setOpenAIPackagesDialog(false)}
      onGenerated={(items)=>{
        setBulkItems(Array.isArray(items) ? items : [items]);
        setOpenAIPackagesDialog(false);
        setOpenBulkDialog(true);
      }}
    />

    <AddInquiryForm
      open={openInquiryForm}
      onClose={() => {
        setOpenInquiryForm(false);
        setSelectedPackageForInquiry(null);
      }}
      packages={packages}
      initialPackage={selectedPackageForInquiry}
      projectName={offer.order_name || ''}
      offerDisplayId={(offer as any).offer_id || (offer.offer_sequence_number && offer.created_at ? `OF.${offer.offer_sequence_number}.${new Date(offer.created_at).getFullYear()}` : offer.id.substring(0, 8))}
      hasCollectifileBoard={!!(offer as any).collectifile_board_id || !!(offer as any).miro_link}
      onAdd={async (inquiryData) => {
        try {
          await offerStandaloneManageService.createInquiry(offer.id, {
            package_id: inquiryData.packageId || undefined,
            contractor_email: inquiryData.contractorEmail,
            contractor_phone_1: inquiryData.contractorPhone1?.trim() || undefined,
            contractor_phone_2: inquiryData.contractorPhone2?.trim() || undefined,
            subject: inquiryData.subject,
            status: 'Przygotowane',
            invite_to_collectifile_board: inquiryData.inviteToCollectifileBoard || false
          });
          showNotification('Zapytanie zostało utworzone pomyślnie', 'success');
          await refreshManageLists();
          setOpenInquiryForm(false);
          setSelectedPackageForInquiry(null);
        } catch (e: any) {
          showNotification(e.response?.data?.message || 'Nie udało się utworzyć zapytania.', 'error');
        }
      }}
    />
    </>
  );
}


